/**
 * Permisos Service - Frontend
 * Servicio para gestión y verificación de permisos en el frontend
 */
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:4004' 
  : '';

const ENDPOINT = 'api/permisos';

// Cache de permisos del usuario en localStorage
const PERMISOS_CACHE_KEY = 'user_permisos';
const PERMISOS_CACHE_TIMESTAMP = 'user_permisos_timestamp';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

/**
 * Obtener permisos del usuario actual
 * @returns {Promise<string[]>} - Array de permisos (ej: ['CARGAINF', 'VERINF'])
 */
export const getMyPermisos = async () => {
  try {
    // Verificar cache
    const cached = localStorage.getItem(PERMISOS_CACHE_KEY);
    const timestamp = localStorage.getItem(PERMISOS_CACHE_TIMESTAMP);
    
    if (cached && timestamp) {
      const age = Date.now() - parseInt(timestamp);
      if (age < CACHE_DURATION) {
        return JSON.parse(cached);
      }
    }

    // Obtener token
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No hay token de autenticación');

    // Consultar API
    const { data } = await axios.get(`${API_URL}/${ENDPOINT}/me`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    console.log('🔐 Respuesta de permisos:', data);
    const permisos = data.data.permisos || [];
    console.log('✅ Permisos extraídos:', permisos);

    // Guardar en cache
    localStorage.setItem(PERMISOS_CACHE_KEY, JSON.stringify(permisos));
    localStorage.setItem(PERMISOS_CACHE_TIMESTAMP, Date.now().toString());

    return permisos;
  } catch (error) {
    console.error('Error al obtener permisos:', error);
    return [];
  }
};

/**
 * Verificar si el usuario tiene un permiso específico
 * @param {string} permiso - Clave del permiso (ej: 'CARGAINF')
 * @returns {Promise<boolean>}
 */
export const tienePermiso = async (permiso) => {
  try {
    const permisos = await getMyPermisos();
    return permisos.includes(permiso);
  } catch (error) {
    console.error('Error al verificar permiso:', error);
    return false;
  }
};

/**
 * Verificar si el usuario tiene al menos uno de los permisos listados
 * @param {string[]} permisos - Array de permisos
 * @returns {Promise<boolean>}
 */
export const tieneAlgunPermiso = async (permisos) => {
  try {
    const misPermisos = await getMyPermisos();
    return permisos.some(p => misPermisos.includes(p));
  } catch (error) {
    console.error('Error al verificar permisos:', error);
    return false;
  }
};

/**
 * Verificar si el usuario tiene todos los permisos listados
 * @param {string[]} permisos - Array de permisos
 * @returns {Promise<boolean>}
 */
export const tieneTodosPermisos = async (permisos) => {
  try {
    const misPermisos = await getMyPermisos();
    return permisos.every(p => misPermisos.includes(p));
  } catch (error) {
    console.error('Error al verificar permisos:', error);
    return false;
  }
};

/**
 * Limpiar cache de permisos (útil al hacer logout o cambiar rol)
 */
export const limpiarCachePermisos = () => {
  localStorage.removeItem(PERMISOS_CACHE_KEY);
  localStorage.removeItem(PERMISOS_CACHE_TIMESTAMP);
};

// ==================== ADMIN: Gestión de Permisos ====================

/**
 * Obtener todos los permisos disponibles
 * @returns {Promise<Array>}
 */
export const getAllPermisos = async () => {
  const token = localStorage.getItem('token');
  const { data } = await axios.get(`${API_URL}/${ENDPOINT}`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data.data.permisos || [];
};

/**
 * Obtener permisos de un rol específico
 * @param {number} rolId 
 * @returns {Promise<Array>}
 */
export const getPermisosByRol = async (rolId) => {
  const token = localStorage.getItem('token');
  const { data } = await axios.get(`${API_URL}/${ENDPOINT}/rol/${rolId}`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data.data.permisos || [];
};

/**
 * Obtener todos los roles con sus permisos
 * @returns {Promise<Array>}
 */
export const getAllRolesWithPermisos = async () => {
  const token = localStorage.getItem('token');
  const { data } = await axios.get(`${API_URL}/${ENDPOINT}/roles`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data.data.roles || [];
};

/**
 * Asignar permiso a un rol
 * @param {number} rolId 
 * @param {number} permisoId 
 * @returns {Promise<Object>}
 */
export const asignarPermiso = async (rolId, permisoId) => {
  const token = localStorage.getItem('token');
  const { data } = await axios.post(`${API_URL}/${ENDPOINT}/asignar`, {
    rolId,
    permisoId
  }, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

/**
 * Remover permiso de un rol
 * @param {number} rolId 
 * @param {number} permisoId 
 * @returns {Promise<Object>}
 */
export const removerPermiso = async (rolId, permisoId) => {
  const token = localStorage.getItem('token');
  const { data } = await axios.delete(`${API_URL}/${ENDPOINT}/remover`, {
    data: { rolId, permisoId },
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

/**
 * Actualizar todos los permisos de un rol
 * @param {number} rolId 
 * @param {number[]} permisosIds 
 * @returns {Promise<Object>}
 */
export const updateRolPermisos = async (rolId, permisosIds) => {
  const token = localStorage.getItem('token');
  const { data } = await axios.put(`${API_URL}/${ENDPOINT}/rol/${rolId}`, {
    permisosIds
  }, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

/**
 * Crear nuevo permiso
 * @param {string} nmPermiso - Nombre del permiso (ej: "NUEVO_PERMISO")
 * @returns {Promise<Object>}
 */
export const createPermiso = async (nmPermiso) => {
  const token = localStorage.getItem('token');
  const { data } = await axios.post(`${API_URL}/${ENDPOINT}`, { nmPermiso }, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

/**
 * Eliminar permiso
 * @param {number} permisoId 
 * @returns {Promise<Object>}
 */
export const deletePermiso = async (permisoId) => {
  const token = localStorage.getItem('token');
  const { data } = await axios.delete(`${API_URL}/${ENDPOINT}/${permisoId}`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

/**
 * Obtener estadísticas de permisos
 * @returns {Promise<Object>}
 */
export const getPermisosStats = async () => {
  const token = localStorage.getItem('token');
  const { data } = await axios.get(`${API_URL}/${ENDPOINT}/stats`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data.data;
};

// Objeto con todas las funciones exportadas
const permisosService = {
  // Usuario
  getMyPermisos,
  tienePermiso,
  tieneAlgunPermiso,
  tieneTodosPermisos,
  limpiarCachePermisos,
  
  // Admin
  getAllPermisos,
  getPermisosByRol,
  getAllRolesWithPermisos,
  asignarPermiso,
  removerPermiso,
  updateRolPermisos,
  createPermiso,
  deletePermiso,
  getPermisosStats
};

export default permisosService;
